import React, { useEffect, useState } from "react";
import {
  Modal,
  TextInput,
  Button,
  Stack,
  Group,
  NumberInput,
  Text,
  Table,
} from "@mantine/core";
import { CostItem } from "../types/common.types";
import { CostResponse } from "../types/responses.type";

interface CostsInputModalProps {
  opened: boolean;
  onClose: () => void;
  onSave: (costs: CostResponse) => void;
  initialCosts?: CostResponse;
  title?: string;
}

const CostsInputModal: React.FC<CostsInputModalProps> = ({
  opened,
  onClose,
  onSave,
  initialCosts = {},
  title = "Edit Costs",
}) => {
  const [costSubject, setCostSubject] = useState<string>(
    initialCosts?.costSubject || "",
  );
  const [costs, setCosts] = useState(initialCosts?.costs || {});
  const [costKeys, setCostKeys] = useState<string[]>(
    Object.keys(initialCosts?.costs || {}),
  );

  useEffect(() => {
    if (opened) {
      setCostSubject(initialCosts?.costSubject || "");
      setCosts(initialCosts?.costs || {});
      setCostKeys(Object.keys(initialCosts?.costs || {}));
    }
  }, [opened, initialCosts]);

  const handleValueChange = (key: string, value: number | string) => {
    setCosts((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        value:
          typeof value === "string" ? parseFloat(value) || undefined : value,
      },
    }));
  };

  const handleSave = () => {
    // Filter out empty entries
    const filteredCosts = Object.entries(costs).reduce(
      (acc, [key, item]) => {
        if (item.value !== undefined || item.uom !== undefined) {
          acc[key] = item;
        }
        return acc;
      },
      {} as { [key: string]: CostItem },
    );

    const response: CostResponse = {
      costSubject: costSubject || undefined,
      costs: filteredCosts,
    };

    onSave(response);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      size="lg"
      scrollAreaComponent={Stack}
    >
      <Stack gap="md">
        {costKeys.length > 0 ? (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Cost Item</Table.Th>
                <Table.Th>Value</Table.Th>
                <Table.Th>Description</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {costKeys.map((key) => (
                <Table.Tr key={key}>
                  <Table.Td fw={500}>{key}</Table.Td>
                  <Table.Td>
                    <NumberInput
                      placeholder="Enter value"
                      value={costs[key]?.value || ""}
                      onChange={(value) => handleValueChange(key, value)}
                      decimalScale={2}
                      min={0}
                      styles={{ input: { width: 120 } }}
                    />
                  </Table.Td>
                  <Table.Td>
                    {costs[key]?.description || costs[key]?.uom || "-"}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        ) : (
          <Text c="dimmed" ta="center">
            No cost items to edit
          </Text>
        )}
      </Stack>

      <Group justify="flex-end" mt="lg">
        <Button variant="default" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSave}>Save</Button>
      </Group>
    </Modal>
  );
};

export default CostsInputModal;
